// components/artifacts/PlanArtifactContent.js - Plan 아티팩트 콘텐츠 컴포넌트

import React from 'react';
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EditorContent } from "@tiptap/react";
import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";
import HighlightOverlay from "./HighlightOverlay";
import ArtifactSelectionMenu from './ArtifactSelectionMenu';
import ArtifactDiffModal from './ArtifactDiffModal';
import { modifyArtifactText } from '../../services/artifactAIService';
import { apiFetch, alertUnlessQuotaExceeded } from '../../services/apiClient';
import { createArtifactEditHistory } from "../../services/artifactHistoryService";
import { smartReplace, findReplacementSpan } from '../../utils/textUtils';
import { parseMarkdownSections } from '../../utils/markdownSections';
import { markdownToHtml } from "../../utils/artifactUtils";

/**
 * Plan 아티팩트 콘텐츠 컴포넌트
 * @param {object} props
 * @param {object} props.artifact - 아티팩트 객체
 * @param {boolean} props.isEditing - 편집 모드 여부
 * @param {boolean} props.isGenerating - 생성 중 여부
 * @param {number} props.studyId - 연구 ID
 * @param {string} props.studyName - 연구 이름
 * @param {function} props.onArtifactUpdate - 아티팩트 업데이트 콜백 (updatedContent) => void
 * @param {function} props.onDirectEdit - 직접 수정 콜백 (selectedText) => void
 */
const PlanArtifactContent = ({
  artifact,
  isEditing,
  isGenerating,
  studyId,
  studyName,
  onArtifactUpdate,
  onDirectEdit,
  onHistoryUpdate, // 히스토리 업데이트 콜백 (history) => void
  editor, // TipTap editor instance (optional)
  dragHintStorageKey = "plan_artifact_drag_hint_seen",
}) => {
  // Step 2: 레거시 드래그 메뉴를 다시 활성화해
  // DOM Range → ProseMirror(from/to) 좌표 기반으로 AI 수정을 정확히 적용한다.
  const ENABLE_LEGACY_DRAG_SELECTION_MENU = true;

  const [diffModalOpen, setDiffModalOpen] = React.useState(false);
  const [diffData, setDiffData] = React.useState({ original: "", modified: "" });
  const [diffSelectedText, setDiffSelectedText] = React.useState("");
  const [diffUserPrompt, setDiffUserPrompt] = React.useState("");
  const [diffSpan, setDiffSpan] = React.useState(null); // { ok, start, end }
  const [isProcessing, setIsProcessing] = React.useState(false);

  // 읽기 모드: 섹션 UX 상태 (헤더 섹션 우선 + 문단 폴백)
  const sections = React.useMemo(
    () => parseMarkdownSections(artifact?.content || ""),
    [artifact?.content]
  );
  const [hoveredSectionId, setHoveredSectionId] = React.useState(null);
  const [activeSectionId, setActiveSectionId] = React.useState(null); // 드래그/포커스된 섹션
  const [sectionMenuOpenFor, setSectionMenuOpenFor] = React.useState(null);
  const [sectionAiTarget, setSectionAiTarget] = React.useState(null); // { id, title, start, end, markdown }
  const [sectionAiPrompt, setSectionAiPrompt] = React.useState("");
  const [sectionAiOpen, setSectionAiOpen] = React.useState(false);

  // 드래그 기반 수정 히스토리
  const [editHistory, setEditHistory] = React.useState([]); // newest first
  const [undoToast, setUndoToast] = React.useState(null); // { prevContent?, mode: 'read'|'edit', anchorFrom?, isUndone?: boolean }
  const [undoToastPlacement, setUndoToastPlacement] = React.useState(null); // { top, left } document coords

  // 섹션 AI 제안 상태
  const [suggestions, setSuggestions] = React.useState([]); // [{ id, sectionId, sectionTitle, prompt, original, modified, span, status, createdAt }]
  const [activeSuggestionId, setActiveSuggestionId] = React.useState(null);

  // 드래그 선택 후: 원본(빨강) + 개선 카드(초록) 플로우
  const [inlineImprove, setInlineImprove] = React.useState(null);
  // {
  //   status: 'loading'|'ready'|'error',
  //   from, to,
  //   prompt,
  //   originalText,
  //   originalRects,
  //   modifiedMarkdown,
  //   placement, // {top,left} absolute
  //   error?
  // }

  // TipTap selection range 추적 (문자열 매칭 없이 적용하기 위해)
  const lastNonEmptySelectionRef = React.useRef(null); // { from, to }
  const requestSelectionRef = React.useRef(null); // AI 요청 당시 selection (freeze)

  const computePlacementFromPos = React.useCallback(
    (fromPos, offsetY = 0) => {
      if (!editor?.view || typeof fromPos !== "number") return null;
      try {
        const coords = editor.view.coordsAtPos(fromPos);
        const containerRect = editor.view.dom?.getBoundingClientRect?.();
        const scrollX = window.scrollX || window.pageXOffset || 0;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        // 문서 좌표(absolute). offsetY만큼 아래로 이동 (기본값 0, 토스트는 아래에 표시)
        return {
          top: coords.bottom + scrollY + offsetY,
          // 카드/토스트는 caret(left) 기준이면 폭 때문에 화면 밖으로 밀릴 수 있어
          // 문서(에디터) 컨테이너의 중앙으로 X를 고정해서 안정적으로 보이게 한다.
          left:
            (containerRect
              ? (containerRect.left + containerRect.width / 2)
              : coords.left) + scrollX,
        };
      } catch (e) {
        return null;
      }
    },
    [editor]
  );

  // 개선 카드 위치는 스크롤/리사이즈에도 선택 위치를 따라가게 업데이트
  React.useEffect(() => {
    if (!inlineImprove?.placement || !editor || !inlineImprove?.to) return;

    const update = () => {
      const p = computePlacementFromPos(inlineImprove.to);
      if (p) setInlineImprove((cur) => (cur ? { ...cur, placement: p } : cur));
    };

    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [inlineImprove?.to, editor, computePlacementFromPos, inlineImprove?.placement]);

  React.useEffect(() => {
    if (!editor) return;

    const onSelectionUpdate = ({ editor: ed }) => {
      try {
        const { from, to } = ed.state.selection || {};
        if (typeof from === "number" && typeof to === "number" && from !== to) {
          lastNonEmptySelectionRef.current = { from, to };
        }
      } catch (e) {
        // ignore
      }
    };

    editor.on("selectionUpdate", onSelectionUpdate);
    return () => {
      try {
        editor.off("selectionUpdate", onSelectionUpdate);
      } catch (e) {
        // ignore
      }
    };
  }, [editor]);

  // 읽기 모드에서: 드래그/선택이 포함된 섹션을 active로 지정
  React.useEffect(() => {
    if (isEditing || isGenerating) return;

    const getSectionIdFromSelection = () => {
      const sel = window.getSelection?.();
      if (!sel || sel.rangeCount === 0) return null;
      const range = sel.getRangeAt(0);
      let node = range.commonAncestorContainer;
      if (!node) return null;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
      if (!node || !node.closest) return null;
      const el = node.closest("[data-section-id]");
      return el?.getAttribute("data-section-id") || null;
    };

    const handleSelectionChange = () => {
      const id = getSectionIdFromSelection();
      if (id) setActiveSectionId(id);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    window.addEventListener("mouseup", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      window.removeEventListener("mouseup", handleSelectionChange);
    };
  }, [isEditing, isGenerating]);

  const getDirectEditAnchorText = (sec) => {
    if (!sec) return "";
    if (sec.title) return sec.title;
    const firstLine = (sec.markdown || "")
      .split("\n")
      .map((l) => l.trim())
      .find((l) => !!l);
    return (firstLine || "").replace(/^#{1,6}\s+/, "");
  };

  const openSectionAiPanel = (sec) => {
    if (!sec) return;
    setSectionAiTarget({
      id: sec.id,
      title: sec.title || "문단",
      start: sec.start,
      end: sec.end,
      markdown: sec.markdown || "",
    });
    setSectionAiPrompt("");
    setSectionAiOpen(true);
    setSectionMenuOpenFor(null);
  };

  const closeSectionAiPanel = () => {
    setSectionAiOpen(false);
    setSectionAiTarget(null);
    setSectionAiPrompt("");
  };

  const submitSectionAi = async () => {
    if (!artifact?.id) throw new Error("아티팩트 ID가 없습니다.");
    if (!sectionAiTarget?.markdown) {
      alert("수정할 섹션을 찾을 수 없습니다.");
      return;
    }
    const prompt = (sectionAiPrompt || "").trim();
    if (!prompt) return;

    setIsProcessing(true);
    try {
      const result = await modifyArtifactText(
        artifact.id,
        sectionAiTarget.markdown,
        prompt,
        artifact.content || ""
      );
      if (!result?.success) throw new Error(result?.error || "AI 수정에 실패했습니다.");
      if (!result.modified) throw new Error("AI 제안 텍스트가 비어있습니다.");

      const sugId = `sug-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const suggestion = {
        id: sugId,
        sectionId: sectionAiTarget.id,
        sectionTitle: sectionAiTarget.title || "문단",
        prompt,
        original: sectionAiTarget.markdown,
        modified: result.modified,
        span: { ok: true, start: sectionAiTarget.start, end: sectionAiTarget.end },
        status: "pending", // pending | applied | rejected
        createdAt: Date.now(),
      };
      setSuggestions((prev) => [suggestion, ...prev]);
      setActiveSuggestionId(sugId);

      setDiffSelectedText(sectionAiTarget.markdown);
      setDiffUserPrompt(prompt);
      setDiffData({ original: sectionAiTarget.markdown, modified: result.modified });
      setDiffSpan({ ok: true, start: sectionAiTarget.start, end: sectionAiTarget.end });
      setDiffModalOpen(true);
      closeSectionAiPanel();
    } catch (e) {
      alertUnlessQuotaExceeded(e, `텍스트 수정 중 오류가 발생했습니다: ${e.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateArtifactContent = async (updatedContent) => {
    if (!artifact?.id) throw new Error("아티팩트 ID가 없습니다.");
    const resp = await apiFetch(`/artifacts/${artifact.id}`, {
      method: "PUT",
      body: JSON.stringify({ content: updatedContent }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: "알 수 없는 오류" }));
      throw new Error(err.error || `HTTP error! status: ${resp.status}`);
    }
    return true;
  };


  // ArtifactSelectionMenu -> 부모: AI 요청 처리 (API 호출 + 모달 오픈)
  const handleAiRequest = async (selectedText, userPrompt, selectionPos, meta) => {
    if (!artifact?.id) throw new Error("아티팩트 ID가 없습니다.");
    setIsProcessing(true);
    try {
      // editor가 있으면(편집 모드) DOM 선택 텍스트 대신 editor selection을 신뢰
      let effectiveSelectedText = selectedText;
      let effectiveContext = artifact.content || "";
      let effectiveSelection = null;
      let selectedMarkdownHint = "";
      let originalRects = meta?.overlay_rects || [];

      if (editor) {
        // 0순위: 메뉴가 뜰 때 저장해 둔 ProseMirror selection(from/to)을 최우선으로 사용
        if (selectionPos && typeof selectionPos.from === "number" && typeof selectionPos.to === "number" && selectionPos.from !== selectionPos.to) {
          effectiveSelection = { from: selectionPos.from, to: selectionPos.to };
          requestSelectionRef.current = { from: effectiveSelection.from, to: effectiveSelection.to };
          effectiveSelectedText = editor.state.doc.textBetween(effectiveSelection.from, effectiveSelection.to, "\n");
          effectiveContext = editor.getText();
        }

        // 모달이 뜨기 전에 selection을 확실히 freeze
        // 1순위: 현재 editor.state.selection (가장 정확)
        // 2순위: lastNonEmptySelectionRef (이전에 저장된 것)
        const currentSel = editor.state.selection;
        const savedSel = lastNonEmptySelectionRef.current;
        
        // 현재 selection이 유효하면 우선 사용
        if (!effectiveSelection && currentSel && typeof currentSel.from === "number" && typeof currentSel.to === "number" && currentSel.from !== currentSel.to) {
          effectiveSelection = { from: currentSel.from, to: currentSel.to };
          effectiveSelectedText = editor.state.doc.textBetween(currentSel.from, currentSel.to, "\n");
        } 
        // 현재 selection이 없으면 저장된 것 사용
        else if (!effectiveSelection && savedSel && typeof savedSel.from === "number" && typeof savedSel.to === "number" && savedSel.from !== savedSel.to) {
          effectiveSelection = { from: savedSel.from, to: savedSel.to };
          effectiveSelectedText = editor.state.doc.textBetween(savedSel.from, savedSel.to, "\n");
        }
        
        if (effectiveSelection) {
          // selection을 확실히 freeze (복사본으로 저장)
          requestSelectionRef.current = { from: effectiveSelection.from, to: effectiveSelection.to };
          // 맥락은 현재 에디터 기준이 더 정확함 (저장 전 변경 포함)
          effectiveContext = editor.getText();
        } else {
          requestSelectionRef.current = null;
        }
      }

      // selection_html -> markdown hint (완전 무손실 목적 아님: “형식 힌트”용)
      if (meta?.selection_html) {
        try {
          const turndownService = new TurndownService({
            headingStyle: "atx",
            bulletListMarker: "*",
            codeBlockStyle: "fenced",
            tableCellPadding: " ",
            tableCellAlignment: "left",
          });
          turndownService.use(gfm);
          selectedMarkdownHint = turndownService.turndown(meta.selection_html);
        } catch (e) {
          selectedMarkdownHint = "";
        }
      }

      const result = await modifyArtifactText(artifact.id, effectiveSelectedText, userPrompt, effectiveContext, {
        selected_markdown_hint: selectedMarkdownHint,
      });

      if (!result?.success) {
        throw new Error(result?.error || "AI 수정에 실패했습니다.");
      }

      // "변경 전"은 사용자가 실제로 선택한 텍스트(프론트 기준)를 사용 (백엔드 original은 신뢰하지 않음)
      const original = effectiveSelectedText;
      const modified = result.modified ?? "";
      if (!modified) throw new Error("AI 제안 텍스트가 비어있습니다.");

      // 팝업 대신: 원본은 빨강, 결과는 초록 개선 카드로 노출
      const sel = requestSelectionRef.current || effectiveSelection || selectionPos;
      const placement = sel?.to ? computePlacementFromPos(sel.to) : null;
      setInlineImprove({
        status: "ready",
        from: sel?.from,
        to: sel?.to,
        prompt: userPrompt,
        originalText: original,
        originalRects,
        modifiedMarkdown: modified,
        placement,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const applyInlineImprove = async () => {
    if (!inlineImprove || !editor) return;
    const { from, to, modifiedMarkdown, prompt, originalText } = inlineImprove;
    if (typeof from !== "number" || typeof to !== "number" || from === to) {
      alert("선택 영역 정보를 찾을 수 없습니다. 다시 드래그해 주세요.");
      return;
    }
    if (!modifiedMarkdown) {
      alert("AI 제안 텍스트가 비어있습니다.");
      return;
    }

    setIsProcessing(true);
    try {
      const prevMarkdown = artifact?.content || "";
      const wasEditable = editor.isEditable;
      if (!wasEditable) {
        try {
          editor.setEditable(true);
        } catch (e) {}
      }

      const html = markdownToHtml(modifiedMarkdown);
      editor.chain().focus().setTextSelection({ from, to }).insertContent(html).run();

      // 삽입 후 커서 위치 (수정된 텍스트의 끝)를 가져옴
      const afterInsertPos = editor.state.selection.to;

      if (!wasEditable) {
        try {
          editor.setEditable(false);
        } catch (e) {}
      }

      // locked 상태를 기본으로 가정: 즉시 저장
      const turndownService = new TurndownService({
        headingStyle: "atx",
        bulletListMarker: "*",
        codeBlockStyle: "fenced",
        tableCellPadding: " ",
        tableCellAlignment: "left",
      });
      turndownService.use(gfm);

      let contentToSave = turndownService.turndown(editor.getHTML());
      contentToSave = contentToSave
        .replace(/\|\s*\|\s*\|\s*\n/g, "|\n")
        .replace(/\|\s*\n/g, "|\n")
        .replace(/\|\s*$/gm, "|");
      contentToSave = contentToSave.replace(/^```markdown\n/, "").replace(/\n```$/, "");
      contentToSave = contentToSave.replace(/^```\n/, "").replace(/\n```$/, "");

      await updateArtifactContent(contentToSave);
      onArtifactUpdate?.(contentToSave);

      // DB 히스토리 저장 (적용 전/후 스냅샷)
      try {
        await createArtifactEditHistory(artifact.id, {
          prompt: prompt || "",
          source: "inline_improve",
          before_markdown: prevMarkdown,
          after_markdown: contentToSave,
          selection_from: from,
          selection_to: to,
        });
      } catch (e) {
        // 저장 실패해도 본문 적용은 유지
        console.warn("[edit_history] save failed:", e);
      }

      // 히스토리에 추가
      const historyItem = {
        id: `edit-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        prompt: prompt || "",
        originalText: originalText || "",
        modifiedMarkdown: modifiedMarkdown,
        from: from,
        to: to,
        status: "applied",
        createdAt: Date.now(),
      };
      const newHistory = [historyItem, ...editHistory].slice(0, 50); // 최대 50개
      setEditHistory(newHistory);
      onHistoryUpdate?.(newHistory);

      // 적용 후에는 원본 빨강/개선 카드 제거
      setInlineImprove(null);
      // Undo 토스트 표시 (수정된 텍스트 바로 아래에)
      setUndoToast({
        mode: "read",
        prevContent: prevMarkdown,
        nextContent: contentToSave,
        anchorFrom: afterInsertPos,
        isUndone: false,
      });
      setUndoToastPlacement(computePlacementFromPos(afterInsertPos, 8));
    } catch (e) {
      alert(`적용 중 오류가 발생했습니다: ${e.message || e}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 에디터에서 텍스트로 selection 범위 찾기 (fallback용)
  const findSelectionByText = (editor, searchText) => {
    if (!editor || !searchText) return null;
    const doc = editor.state.doc;
    const needle = searchText.trim();
    if (!needle) return null;

    // ProseMirror doc에서 텍스트 찾기
    let found = null;
    doc.descendants((node, pos) => {
      if (found) return false;
      if (!node.isText || !node.text) return true;
      const idx = node.text.indexOf(needle);
      if (idx !== -1) {
        const from = pos + idx;
        const to = from + needle.length;
        found = { from, to };
        return false;
      }
      return true;
    });
    return found;
  };

  const handleApplyChanges = async () => {
    // ✅ editor가 있으면: 문자열 매칭 없이 selection range에 바로 덮어쓰기
    if (editor) {
      const { original, modified } = diffData || {};
      if (!modified) {
        alert("AI 제안 텍스트가 비어있습니다.");
        return;
      }

      // 1순위: 저장된 selection range 사용
      let sel = requestSelectionRef.current;
      
      // 2순위: "변경 전" 텍스트로 에디터에서 다시 찾기 (fallback)
      if (!sel || typeof sel.from !== "number" || typeof sel.to !== "number" || sel.from === sel.to) {
        if (original) {
          console.log('[handleApplyChanges] 저장된 selection이 없음, 텍스트로 재검색:', original.substring(0, 50));
          sel = findSelectionByText(editor, original);
        }
      }

      if (!sel || typeof sel.from !== "number" || typeof sel.to !== "number" || sel.from === sel.to) {
        alert("선택 영역 정보를 찾을 수 없습니다. 다시 드래그 후 시도해 주세요.");
        return;
      }

      setIsProcessing(true);
      try {
        // locked(읽기)에서 적용한 경우: 바로 저장까지 해서 결과물이 유지되게 한다.
        const isLocked = !isEditing;
        const prevMarkdown = artifact?.content || "";

        // editor가 non-editable이라 insert가 막히는 환경을 대비해
        // 잠깐 editable을 켰다가(필요 시) 다시 끈다.
        const wasEditable = editor.isEditable;
        if (!wasEditable) {
          try {
            editor.setEditable(true);
          } catch (e) {
            // ignore
          }
        }

        // TipTap insertContent는 HTML을 기대한다.
        // (markdown 문자열을 그대로 넣으면 \n/헤딩 기호 등이 텍스트로 삽입되어 "마크다운이 깨진 것처럼" 보일 수 있음)
        const html = markdownToHtml(modified);
        editor
          .chain()
          .focus()
          .setTextSelection({ from: sel.from, to: sel.to })
          .insertContent(html)
          .run();

        // “적용됨 · 되돌리기”는 적용된 위치(선택 시작점) 근처에 띄운다
        setUndoToastPlacement(computePlacementFromPos(sel.from));

        // 다시 locked로 복귀
        if (!wasEditable) {
          try {
            editor.setEditable(false);
          } catch (e) {
            // ignore
          }
        }

        if (isLocked) {
          // AI 적용 결과를 turndown으로 markdown 변환 후 즉시 저장
          const turndownService = new TurndownService({
            headingStyle: "atx",
            bulletListMarker: "*",
            codeBlockStyle: "fenced",
            tableCellPadding: " ",
            tableCellAlignment: "left",
          });
          turndownService.use(gfm);

          let contentToSave = turndownService.turndown(editor.getHTML());
          contentToSave = contentToSave
            .replace(/\|\s*\|\s*\|\s*\n/g, "|\n")
            .replace(/\|\s*\n/g, "|\n")
            .replace(/\|\s*$/gm, "|");
          contentToSave = contentToSave.replace(/^```markdown\n/, "").replace(/\n```$/, "");
          contentToSave = contentToSave.replace(/^```\n/, "").replace(/\n```$/, "");

          await updateArtifactContent(contentToSave);
          onArtifactUpdate?.(contentToSave);
          setUndoToast({
            mode: "read",
            prevContent: prevMarkdown,
            nextContent: contentToSave,
            anchorFrom: sel.from,
            isUndone: false,
          });

          // DB 히스토리 저장 (적용 전/후 스냅샷)
          try {
            await createArtifactEditHistory(artifact.id, {
              prompt: diffUserPrompt || "",
              source: "diff_modal",
              before_markdown: prevMarkdown,
              after_markdown: contentToSave,
              selection_from: sel.from,
              selection_to: sel.to,
            });
          } catch (e) {
            console.warn("[edit_history] save failed:", e);
          }
        } else {
          // 편집 모드: undo는 editor history 사용
          setUndoToast({ mode: "edit", anchorFrom: sel.from, isUndone: false });
        }

        setDiffModalOpen(false);
        requestSelectionRef.current = null;

        if (activeSuggestionId) {
          setSuggestions((prev) =>
            prev.map((s) => (s.id === activeSuggestionId ? { ...s, status: "applied" } : s))
          );
          setActiveSuggestionId(null);
        }
      } catch (e) {
        alert(`적용 중 오류가 발생했습니다: ${e.message || e}`);
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    const current = artifact?.content || "";
    const { original, modified } = diffData || {};
    
    // "변경 전"으로 화면에 보여준 값(original)을 우선 사용
    // (유저가 보는 기준값과 apply 기준값을 동일하게 맞춤)
    const searchText = original || diffSelectedText || "";
    if (!searchText) {
      alert("선택한 텍스트를 찾을 수 없습니다.");
      return;
    }

    console.log('[handleApplyChanges] 치환 시도', {
      searchTextLength: searchText.length,
      originalLength: original?.length,
      modifiedLength: modified?.length,
      contentLength: current.length,
      searchTextPreview: searchText.substring(0, 100),
      originalPreview: original?.substring(0, 100),
      searchTextFull: searchText, // 전체 텍스트도 로그에 포함
      contentPreview: current.substring(0, 500) // 더 긴 프리뷰
    });

    // 0순위: 저장해둔 span(인덱스)이 있으면, 그 위치를 바로 교체 (가장 빠름)
    let replaced = { ok: false, content: current };
    if (diffSpan?.ok && Number.isFinite(diffSpan.start) && Number.isFinite(diffSpan.end)) {
      const start = Math.max(0, Math.min(current.length, diffSpan.start));
      const end = Math.max(start, Math.min(current.length, diffSpan.end));
      // 최소한의 안전장치: 범위가 너무 작거나 이상하면 fallback
      if (end > start) {
        replaced = {
          ok: true,
          content: current.substring(0, start) + modified + current.substring(end),
        };
      }
    }

    // 1순위: 문자열 기반 스마트 치환
    if (!replaced.ok) {
      replaced = smartReplace(current, searchText, modified);
    }
    
    // 2순위: 백엔드 original로 시도
    if (!replaced.ok && original && original !== searchText) {
      console.log('[handleApplyChanges] 첫 시도 실패, original로 재시도');
      replaced = smartReplace(current, original, modified);
    }
    
    if (!replaced.ok) {
      console.error('[handleApplyChanges] 치환 실패', { 
        searchTextLength: searchText.length,
        originalLength: original?.length,
        contentLength: current.length,
        searchTextPreview: searchText.substring(0, 100), 
        originalPreview: original?.substring(0, 100),
        contentPreview: current.substring(0, 200)
      });
      alert(`선택한 텍스트 위치를 찾을 수 없습니다.\n\n선택한 텍스트 길이: ${searchText.length}자\n문서 전체 길이: ${current.length}자\n\n다시 선택해서 시도해 주세요.`);
      return;
    }

    setIsProcessing(true);
    try {
      const prevContent = current;
      await updateArtifactContent(replaced.content);
      if (onArtifactUpdate) {
        onArtifactUpdate(replaced.content);
      }
      setDiffModalOpen(false);
      setUndoToast({ mode: "read", prevContent });

      // DB 히스토리 저장 (적용 전/후 스냅샷)
      try {
        await createArtifactEditHistory(artifact.id, {
          prompt: diffUserPrompt || "",
          source: "diff_modal",
          before_markdown: prevContent,
          after_markdown: replaced.content,
          selection_from: diffSpan?.ok ? diffSpan.start : null,
          selection_to: diffSpan?.ok ? diffSpan.end : null,
        });
      } catch (e) {
        console.warn("[edit_history] save failed:", e);
      }

      if (activeSuggestionId) {
        setSuggestions((prev) =>
          prev.map((s) => (s.id === activeSuggestionId ? { ...s, status: "applied" } : s))
        );
        setActiveSuggestionId(null);
      }
    } catch (e) {
      alert(`저장 중 오류가 발생했습니다: ${e.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 드래그 기능 안내 표시 여부 (로컬 스토리지로 관리)
  const [showDragHint, setShowDragHint] = React.useState(() => {
    const hasSeenHint = localStorage.getItem(dragHintStorageKey);
    return !hasSeenHint;
  });

  const dismissDragHint = () => {
    setShowDragHint(false);
    localStorage.setItem(dragHintStorageKey, 'true');
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* 드래그 기능 안내 배너 */}
      {showDragHint && !isEditing && !isGenerating && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, rgba(58, 102, 255, 0.08) 0%, rgba(124, 156, 232, 0.12) 100%)',
            border: '1px solid rgba(58, 102, 255, 0.2)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <div style={{ fontSize: '20px' }}>✨</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '4px', fontSize: '14px' }}>
                텍스트를 드래그하여 AI로 수정하세요
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>
                원하는 텍스트를 선택하면 AI 수정 메뉴가 나타납니다. AI는 전체 맥락을 고려하여 추론하고 더 풍부하게 작성해드립니다.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={dismissDragHint}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: 1,
              borderRadius: '6px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(0,0,0,0.05)')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
            aria-label="닫기"
          >
            ×
          </button>
        </div>
      )}

      <ArtifactDiffModal
        open={diffModalOpen}
        data={diffData}
        isProcessing={isProcessing}
        onClose={() => {
          if (isProcessing) return;
          setDiffModalOpen(false);
          setActiveSuggestionId(null);
        }}
        onApply={handleApplyChanges}
      />

      {/* 드래그 원본 하이라이트(연한 빨강): 개선 카드가 열려있는 동안 유지 */}
      {inlineImprove?.originalRects?.length > 0 && (
        <HighlightOverlay rects={inlineImprove.originalRects} color="rgba(239, 68, 68, 0.16)" zIndex={65} />
      )}

      {/* 개선 카드(연한 초록): 선택한 위치 아래에 붙여서 보여줌 */}
      {inlineImprove?.placement &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: `${inlineImprove.placement.top + 22}px`,
              left: `${inlineImprove.placement.left}px`,
              transform: "translate(-50%, 0)",
              zIndex: 80,
              width: "min(1040px, 96vw)",
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                border: "1px solid rgba(16, 185, 129, 0.35)",
                background: "rgba(236, 253, 245, 0.92)",
                borderRadius: "16px",
                boxShadow: "0 18px 44px rgba(0,0,0,0.14)",
                padding: "16px",
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
                <div style={{ fontWeight: 900, color: "#064e3b" }}>AI 개선 제안</div>
                <button
                  type="button"
                  onClick={() => setInlineImprove(null)}
                  disabled={isProcessing}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: "18px",
                    cursor: "pointer",
                    color: "#047857",
                  }}
                  aria-label="닫기"
                >
                  ×
                </button>
              </div>
              <div style={{ fontSize: "12px", color: "#065f46", marginTop: "6px" }}>
                요청: {inlineImprove.prompt}
              </div>

              <div
                style={{
                  marginTop: "10px",
                  background: "rgba(255,255,255,0.85)",
                  border: "1px solid rgba(16, 185, 129, 0.18)",
                  borderRadius: "12px",
                  padding: "12px",
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{inlineImprove.modifiedMarkdown || ""}</ReactMarkdown>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={() => setInlineImprove(null)}
                  disabled={isProcessing}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "999px",
                    border: "1px solid rgba(16, 185, 129, 0.25)",
                    background: "rgba(255,255,255,0.9)",
                    fontWeight: 900,
                    cursor: isProcessing ? "not-allowed" : "pointer",
                    color: "#065f46",
                  }}
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={applyInlineImprove}
                  disabled={isProcessing}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "999px",
                    border: "1px solid #10b981",
                    background: "#10b981",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: isProcessing ? "not-allowed" : "pointer",
                  }}
                >
                  이걸로 교체
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* 적용됨 · 되돌리기: "수정된 위치" 근처에 표시 (문서 좌표 absolute) */}
      {undoToast &&
        (undoToastPlacement ? (
          createPortal(
            <div
              style={{
                position: "absolute",
                top: `${undoToastPlacement.top}px`,
                left: `${undoToastPlacement.left}px`,
                transform: "translate(-50%, -100%)",
                zIndex: 90,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  padding: "10px 12px",
                  borderRadius: "999px",
                  border: "1px solid rgba(58, 102, 255, 0.25)",
                  background: "rgba(255,255,255,0.96)",
                  boxShadow: "0 14px 34px rgba(0,0,0,0.14)",
                  pointerEvents: "auto",
                  backdropFilter: "blur(6px)",
                }}
              >
                <button
                  type="button"
                  onClick={async () => {
                    const setEditorFromMarkdown = (md) => {
                      if (!editor) return;
                      try {
                        const wasEditable = editor.isEditable;
                        if (!wasEditable) {
                          try {
                            editor.setEditable(true);
                          } catch (e) {}
                        }
                        const html = markdownToHtml(md || "");
                        editor.commands.setContent(html);
                        if (!wasEditable) {
                          try {
                            editor.setEditable(false);
                          } catch (e) {}
                        }
                      } catch (e) {
                        // ignore
                      }
                    };

                    try {
                      // 되돌리기만 제공 (되돌린 뒤 "다시 적용"은 제공하지 않음)
                      if (undoToast.mode === "edit" && editor) {
                        editor.commands.undo();
                      } else if (undoToast.mode === "read" && undoToast.prevContent != null) {
                        setIsProcessing(true);
                        await updateArtifactContent(undoToast.prevContent);
                        onArtifactUpdate?.(undoToast.prevContent);
                        setEditorFromMarkdown(undoToast.prevContent);
                      }

                      // 히스토리에서 가장 최근 항목을 undone으로 표시
                      setEditHistory((prev) => {
                        const updated = [...prev];
                        if (updated.length > 0 && updated[0].status === "applied") {
                          updated[0] = { ...updated[0], status: "undone" };
                        }
                        return updated;
                      });
                      onHistoryUpdate?.((prev) => {
                        const updated = [...prev];
                        if (updated.length > 0 && updated[0].status === "applied") {
                          updated[0] = { ...updated[0], status: "undone" };
                        }
                        return updated;
                      });

                      // 완료 후 토스트 닫기
                      setUndoToast(null);
                      setUndoToastPlacement(null);
                    } catch (e) {
                      alert(`되돌리기 중 오류가 발생했습니다: ${e.message}`);
                    } finally {
                      setIsProcessing(false);
                    }
                  }}
                  disabled={
                    isProcessing ||
                    (undoToast.mode === "read" && undoToast.prevContent == null)
                  }
                  style={{
                    padding: "8px 12px",
                    borderRadius: "999px",
                    border: "1px solid rgba(58, 102, 255, 0.35)",
                    background: "rgba(239, 246, 255, 0.95)",
                    fontWeight: 800,
                    cursor: isProcessing ? "not-allowed" : "pointer",
                    color: "#1d4ed8",
                  }}
                >
                  되돌리기
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUndoToast(null);
                    setUndoToastPlacement(null);
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "999px",
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    fontSize: "13px",
                    fontWeight: 800,
                    cursor: "pointer",
                    color: "#6b7280",
                  }}
                  aria-label="닫기"
                >
                  닫기
                </button>
              </div>
            </div>,
            document.body
          )
        ) : (
          // placement를 못 잡은 경우 fallback (기존처럼 상단)
          <div style={{ marginBottom: "10px", display: "flex", justifyContent: "center" }}>
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                padding: "10px 12px",
                borderRadius: "999px",
                border: "1px solid rgba(58, 102, 255, 0.25)",
                background: "rgba(255,255,255,0.96)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.14)",
              }}
            >
              <button
                type="button"
                onClick={async () => {
                  try {
                    // fallback은 위치를 못 잡은 경우라서 UI만 상단에 뜨는 형태
                    // 동작은 동일하게 "되돌리기"만 제공
                    if (undoToast?.mode === "edit" && editor) {
                      editor.commands.undo();
                    } else if (undoToast?.mode === "read" && undoToast?.prevContent != null) {
                      setIsProcessing(true);
                      await updateArtifactContent(undoToast.prevContent);
                      onArtifactUpdate?.(undoToast.prevContent);
                      try {
                        const wasEditable = editor?.isEditable;
                        if (editor && !wasEditable) {
                          try {
                            editor.setEditable(true);
                          } catch (e) {}
                        }
                        if (editor) editor.commands.setContent(markdownToHtml(undoToast.prevContent || ""));
                        if (editor && !wasEditable) {
                          try {
                            editor.setEditable(false);
                          } catch (e) {}
                        }
                      } catch (e) {
                        // ignore
                      }
                    }

                    setEditHistory((prev) => {
                      const updated = [...prev];
                      if (updated.length > 0 && updated[0].status === "applied") {
                        updated[0] = { ...updated[0], status: "undone" };
                      }
                      return updated;
                    });
                    onHistoryUpdate?.((prev) => {
                      const updated = [...prev];
                      if (updated.length > 0 && updated[0].status === "applied") {
                        updated[0] = { ...updated[0], status: "undone" };
                      }
                      return updated;
                    });

                    setUndoToast(null);
                    setUndoToastPlacement(null);
                  } catch (e) {
                    alert(`되돌리기 중 오류가 발생했습니다: ${e.message}`);
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing || (undoToast?.mode === "read" && undoToast?.prevContent == null)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "999px",
                  border: "1px solid rgba(58, 102, 255, 0.35)",
                  background: "rgba(239, 246, 255, 0.95)",
                  fontWeight: 800,
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  color: "#1d4ed8",
                }}
              >
                되돌리기
              </button>
              <button
                type="button"
                onClick={() => {
                  setUndoToast(null);
                  setUndoToastPlacement(null);
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: "999px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontSize: "13px",
                  fontWeight: 800,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
                aria-label="닫기"
              >
                닫기
              </button>
            </div>
          </div>
        ))}

      {/* 섹션 AI 입력: 인라인 패널 대신 모달(overlay) */}
      {sectionAiOpen && sectionAiTarget && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 80,
            background: "rgba(17, 24, 39, 0.45)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "min(760px, 94vw)",
              background: "#fff",
              borderRadius: "16px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
              padding: "16px",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
              <div>
                <div style={{ fontWeight: 900, color: "#111827" }}>이 섹션을 어떻게 보강할까요?</div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  대상: {sectionAiTarget.title || "문단"}
                </div>
              </div>
              <button
                type="button"
                onClick={closeSectionAiPanel}
                disabled={isProcessing}
                aria-label="닫기"
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  fontSize: "18px",
                  lineHeight: 1,
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <textarea
              value={sectionAiPrompt}
              onChange={(e) => setSectionAiPrompt(e.target.value)}
              placeholder="예: 근거를 더 추가하고, 조사 대상/방법을 더 구체화해줘"
              rows={6}
              style={{
                marginTop: "12px",
                width: "100%",
                borderRadius: "12px",
                border: "1px solid #d1d5db",
                padding: "12px",
                fontSize: "14px",
                lineHeight: 1.55,
                resize: "vertical",
              }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
              <button
                type="button"
                onClick={closeSectionAiPanel}
                disabled={isProcessing}
                style={{
                  padding: "10px 12px",
                  borderRadius: "12px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  fontWeight: 800,
                  cursor: isProcessing ? "not-allowed" : "pointer",
                }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={submitSectionAi}
                disabled={isProcessing || !sectionAiPrompt.trim()}
                style={{
                  padding: "10px 12px",
                  borderRadius: "12px",
                  border: "1px solid #3A66FF",
                  background: "#3A66FF",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: isProcessing ? "not-allowed" : "pointer",
                }}
              >
                {isProcessing ? "생성 중..." : "제안 생성"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Plan 읽기 화면도 Tiptap(read-only)로 렌더링 */}
      {editor ? (
        <div>
          {/* (Undo 토스트는 selection 근처에 포탈로 렌더링) */}

          {/* 제안 카드(최근) */}
          {suggestions.length > 0 && (
            <div style={{ margin: "10px 0 14px 0" }}>
              <div style={{ fontWeight: 900, color: "#111827", marginBottom: "8px" }}>
                AI 제안
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {suggestions.slice(0, 5).map((s) => (
                  <div
                    key={s.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "14px",
                      background: "#ffffff",
                      padding: "12px",
                      boxShadow: "0 8px 18px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <div style={{ fontWeight: 800, color: "#111827" }}>
                        {s.sectionTitle}
                        <span style={{ marginLeft: "8px", fontSize: "12px", color: "#6b7280" }}>
                          {s.status === "pending" ? "대기" : s.status === "applied" ? "적용됨" : "거절됨"}
                        </span>
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>
                        {s.prompt}
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "10px" }}>
                      {s.status === "pending" && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveSuggestionId(s.id);
                              setDiffSelectedText(s.original);
                              setDiffUserPrompt(s.prompt);
                              setDiffData({ original: s.original, modified: s.modified });
                              setDiffSpan(s.span);
                              setDiffModalOpen(true);
                            }}
                            style={{
                              padding: "8px 10px",
                              borderRadius: "12px",
                              border: "1px solid #3A66FF",
                              background: "#3A66FF",
                              color: "#fff",
                              fontWeight: 900,
                              cursor: "pointer",
                            }}
                          >
                            적용하기
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setSuggestions((prev) =>
                                prev.map((x) => (x.id === s.id ? { ...x, status: "rejected" } : x))
                              )
                            }
                            style={{
                              padding: "8px 10px",
                              borderRadius: "12px",
                              border: "1px solid #e5e7eb",
                              background: "#f9fafb",
                              fontWeight: 900,
                              cursor: "pointer",
                            }}
                          >
                            거절
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className={`plan-tiptap-viewer ${isEditing ? "plan-tiptap-viewer--editing" : ""}`}
            style={{
              border: isEditing ? "2px solid #7C9CE8" : "1px solid rgba(0,0,0,0.06)",
              borderRadius: "12px",
              padding: isEditing ? "10px" : "0px",
              background: "#ffffff",
              minHeight: "600px",
              boxShadow: isEditing ? "0 10px 24px rgba(0,0,0,0.08)" : "none",
            }}
          >
            <EditorContent
              editor={editor}
              style={{
                padding: "20px",
                fontSize: "15px",
                lineHeight: 1.7,
                minHeight: "560px",
              }}
            />
          </div>
        </div>
      ) : (
        // editor가 없으면 기존 Markdown 렌더 fallback
        <div>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {artifact?.content || ""}
          </ReactMarkdown>
        </div>
      )}
      
      {/* [Legacy] 드래그 기반 선택 메뉴 (비활성화) */}
      {ENABLE_LEGACY_DRAG_SELECTION_MENU && !isGenerating && !inlineImprove && (
        <ArtifactSelectionMenu
          enabled={!isGenerating && !isEditing && !inlineImprove}
          artifact={artifact}
          onDirectEdit={onDirectEdit}
          onAiRequest={handleAiRequest}
          editor={editor}
        />
      )}
    </div>
  );
};

export default PlanArtifactContent;
